<template>
  <l-map
    ref="map"
    :class="mapStyle"
    :style="style"
    :zoom="zoom"
    :max-zoom="maxZoom"
    :center="center"
    :options="mapOptions"
    :preferCanvas="preferCanvas"
  >
    <l-control-layers position="topright"></l-control-layers>

    <l-tile-layer
      v-for="tile in tileProviders"
      layer-type="base"
      :key="tile.name"
      :visible="tile.visible"
      :url="tile.url"
      :name="tile.name"
      :attribution="tile.attribution"
    ></l-tile-layer>

    <template v-for="(vehicle, index) in vehicles">
      <l-marker
        v-if="vehicle.shouldDisplayOnMap()"
        :key="index"
        :lat-lng="latLng(vehicle.latitude, vehicle.longitude)"
        :icon="getVehicleIcon(vehicle)"
        :options="{ id: vehicle.imei || vehicle.vehicleId }"
        @click="getVehicleLocation(vehicle)"
      >
        <template v-if="showTooltips && vehicle.shouldDisplayOnMap()">
          <template v-if="showDriverNameOnLabels">
            <l-tooltip :options="tooltipOptions" :style="[vehicleHasIOProperties(vehicle) ? {'background-color': getVehicleIoColor(vehicle)} : {} ]">{{ vehicle.driverName }}</l-tooltip>
          </template>
          <template v-else>
            <l-tooltip :options="tooltipOptions" :style="[vehicleHasIOProperties(vehicle) ? {'background-color': getVehicleIoColor(vehicle)} : {} ]">{{ vehicle.vehicleLabel}}</l-tooltip>
          </template>
        </template>
        <l-popup :options="popupOptions">
          <ImeiComponent :singleImei="vehicle" style="width:420px; height:250px; overflow-x: hidden; overflow-y: hidden;"/>
        </l-popup>
      </l-marker>
    </template>

      <!-- v-if="false" for a reason. If anytime client wanted to se vehicle movement trail, just remove v-if -->
    <template v-if="false" v-for="route in realTimeRoutes">
      <l-polyline
        v-if="needToDisplayRoute(route)"
        :key="route.id"
        :lat-lngs="route.coordinates"
        :color="route.color"
      ></l-polyline>
    </template>

    

    <l-polygon v-if="polygon.visible" :lat-lngs="polygon.coordinates" :color="polygon.color"></l-polygon>

    <template v-for="polyline in polylines">
      <l-polyline
        v-if="polyline.visible"
        :key="polyline.id"
        :lat-lngs="polyline.coordinates"
        :color="polyline.color"
      ></l-polyline>
    </template>

    <v-dialog width="360" v-model="showResultDialogLocal">
      <v-card>
        <v-card-text class="pt-6 pb-0">
          <v-simple-table dense height="auto">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left text-caption font-weight-bold">#</th>
                  <th class="text-left text-caption font-weight-bold">{{labels.headers.driver}}</th>
                  <th
                    class="text-left text-caption font-weight-bold"
                  >{{labels.headers.vehicleLabel}}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in selectedVehiclesToDisplay" :key="index">
                  <td class="text-caption font-weight-regular">{{index+1}}</td>
                  <td class="text-caption font-weight-regular">{{ item.driver }}</td>
                  <td class="text-caption font-weight-regular">{{ item.vehicleLabel }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-row justify="end" no-gutters>
            <v-btn color="green darken-1" text @click="closeResultDialog">Zatvori</v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <template>
      <v-dialog v-model="dialog.show" @keydown.esc="onCloseDialogHandler" max-width="380">
        <template v-if="dialog.show">
          <v-card flat tile>
            <BaseDialogTitle @close="onCloseDialogHandler">
              <template v-slot:title>{{labels.poiDetails}}</template>
            </BaseDialogTitle>
            <v-card-text>
              <MapCrudComponent
                mapType="leaflet"
                :mapData="mapCrudData"
                @closeMapCrudComponent="handleMapCrudComponentClose"
                :submited="dialog.submited"
                :closed="!dialog.show"
                @resetSubmit="dialog.submited=false"
              ></MapCrudComponent>
            </v-card-text>
            <v-card-actions>
              <SubmitGroup
                @close="onCloseDialogHandler"
                :closeOnSubmit="closeOnSubmit"
                @submit="onSubmitDialogHandler"
              />
            </v-card-actions>
          </v-card>
        </template>
      </v-dialog>
    </template>
  </l-map>
</template>

<script>
import { mapState } from 'vuex'
import {
  LMap,
  LTileLayer,
  LControlLayers,
  LMarker,
  LPolyline,
  LPopup,
  LTooltip,
  LPolygon
} from 'vue2-leaflet'
import { latLng } from 'leaflet'
import { getVehicleIcon } from '../../../utils/map/getVehicleIcon'
import options from '../../../resource/maps/leafletOptions'
import BaseLeafletMapComponent from '../BaseLeafletMapComponent'
import EventBus from '../../../event-bus'
import translate from '@/utils/genTable/multilanguageHelper.js'
import store from '@/store/store'
import httpClient from '../../../api/httpClient'
import Vue from 'vue'

export default {
  name: 'LeafletRealTimeMapComponent',
  props: {
    height: Number | String
  },
  extends: BaseLeafletMapComponent,
  components: {
    LMap,
    LTileLayer,
    LControlLayers,
    LMarker,
    LPolyline,
    LPopup,
    LTooltip,
    LPolygon,
    ImeiComponent: () => import('../../map/imei/ImeiComponent')
  },
  data() {
    return {
      zoom: null,
      maxZoom: null,
      center: null,
      tileProviders: [],
      mapOptions: {
        zoomSnap: null
      },
      popupOptions: {
        maxWidth: 450,
        maxHeight: 550
      },
      latLng: latLng,
      getVehicleIcon: getVehicleIcon,
      tooltipOptions: {
        permanent: true,
        direction: 'top'
      },
      tooltipsLoaded: false,
      showDriverLabel: false,
      labels: {
        headers: {
          driver: translate('Driver Name', 'realtimeMap'),
          vehicleLabel: translate('Vehicle Label', 'realtimeMap')
        }
      },
      preferCanvas: false
    }
  },
  computed: {
    ...mapState('map', [
      'vehicles',
      'realTimeRoutes',
      'showDriverNameOnLabels',
      'showTooltips',
      'zoomMap',
      'zoomToVisible'
    ]),
    ...mapState('selectArea', [
      'polygon',
      'polylines',
      'selectedVehiclesToDisplay',
      'mapObject',
      'showResultDialog'
    ]),
    style() {
      let style = 'width: 100%;'
      if (this.height == 'auto') {
        style += 'height: 100vh'
      } else {
        style += 'height:' + this.height + 'px;'
      }
      return style
    },
    showResultDialogLocal: {
      get() {
        return this.showResultDialog
      },
      set() {
        this.closeResultDialog()
      }
    },
    mapStyle() {
      return document.body.clientWidth < 800 ? '' : 'ml-2'
    }
  },
  created() {
    let { zoom, maxZoom, zoomSnap, center, tileProviders, numberOfVehiclesOnMapToPreferCanvas } = options

    this.zoom = zoom
    this.maxZoom = maxZoom
    this.center = latLng(center.lat, center.lng)
    this.tileProviders = tileProviders
    this.mapOptions.zoomSnap = zoomSnap

    if (this.vehicles > numberOfVehiclesOnMapToPreferCanvas) {
      this.preferCanvas = true
    }
  },
  mounted() {
    EventBus.$on('VEHICLE_STREAM_PACKET_ARRIVED', this.handlePacketArrived)
    store.dispatch('selectArea/setMapObject', this.$refs.map)
    EventBus.$on('SET_ZOOM_LEVEL', this.handleZoomLevel)
    setTimeout(() => {
      store.dispatch('map/zoomToAllVisibleVehicles')
    }, 2500)
  },
  watch: {
    showTooltips: {
      handler(value) {
        this.tooltipOptions.permanent = value

        this.$refs.map.mapObject.eachLayer(layer => {
          if (layer instanceof window.L.Marker) {
            layer.unbindTooltip()
          }
        })
      }
    },
    zoomMap: {
      handler() {
        let numberOfFocusedVehicles = []
        this.vehicles.forEach(vehicle => {
          if (vehicle.focused) {
            numberOfFocusedVehicles.push(vehicle)
          }
        })
        if (numberOfFocusedVehicles.length == 1) {
          this.$refs.map.mapObject.setView(
            [
              numberOfFocusedVehicles[0].latitude,
              numberOfFocusedVehicles[0].longitude
            ],
            14
          )
        } else {
          this.setZoomLevelToFocusedVehicles()
        }
      }
    },
    zoomToVisible: {
      handler() {
        this.setZoomLevelToVisibleVehicles()
      }
    }
  },
  methods: {
    handleZoomLevel() {
      this.setZoomLevelBasedOnAllVehicles()
    },
    closeResultDialog() {
      store.dispatch('selectArea/showResultDialog', false)
    },
    needToDisplayRoute(item) {
      let focusedVehicles = this.vehicles.filter(x => !x.focused)
      if (focusedVehicles.length == this.vehicles.length && item.visible) {
        return true
      }
      // marker id is imei-or-vehicleId (see :options above)
      let vehicle = this.vehicles.filter(x => (x.imei || x.vehicleId) == item.id)[0]
      if (!vehicle) {
        return false
      }
      if (item.visible && vehicle.focused) {
        return true
      }
      return false
    },
    handlePacketArrived(vehicleData) {
      let that = this
      let numberOfFocusedVehicles = []
      that.vehicles.forEach(vehicle => {
        if (vehicle.focused) {
          numberOfFocusedVehicles.push(vehicle)
        }
      })
      if (numberOfFocusedVehicles.length == 1) {
        if ((numberOfFocusedVehicles[0].imei || numberOfFocusedVehicles[0].vehicleId) == (vehicleData.imei || vehicleData.vehicleId)) {
          let zoom = that.$refs.map.mapObject.getZoom()
          that.$refs.map.mapObject.setView(
            [
              numberOfFocusedVehicles[0].latitude,
              numberOfFocusedVehicles[0].longitude
            ],
            zoom
          )
        }
      } else {
        let toZoom = false
        for (let vehicle of numberOfFocusedVehicles) {
          if ((vehicle.imei || vehicle.vehicleId) == (vehicleData.imei || vehicleData.vehicleId)) {
            toZoom = true
            break
          }
        }

        if (toZoom) {
          that.setZoomLevelToFocusedVehicles(false)
        }
      }
    },
    async getVehicleLocation(vehicle) {
      let coordinates = [
        {
          latitude: vehicle.latitude,
          longitude: vehicle.longitude
        }
      ]

      let coordinatesResponse = await httpClient.post(
        'lookup/locations',
        coordinates
      )
      let currentLocation = coordinatesResponse.data[0]
      if (currentLocation) {
        Vue.set(vehicle, 'currentLocation', currentLocation.address)
      }
    },
    vehicleHasIOProperties(vehicle) {
      return vehicle.ioProperties.length
    },
    getVehicleIoColor(vehicle) {
      if (!vehicle.ioProperties.length) {
        return ''
      }
      return vehicle.ioProperties[0].ioColor
        ? vehicle.ioProperties[0].ioColor
        : vehicle.ioProperties[1] && vehicle.ioProperties[1].ioColor
          ? vehicle.ioProperties[1].ioColor
          : ''
    }
  }
}
</script>

<style src="leaflet/dist/leaflet.css">
</style>
<style src="../../../assets/icons.css">
</style>

<style>
.vue2leaflet-map {
  z-index: 1 !important;
}
</style>
